import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Team, Issue, ViewerRelation } from 'util/constants';
import { useUser } from 'util/UserContext';
import ActorAvatar from 'components/Actors/ActorAvatar';
import { ACTOR_AVATAR_SIZES } from 'util/actorAvatarSizes';
import { toActorFromTeam } from 'util/actorIdentity';
import DetailPanel from 'ui/DetailPanel';
import api from 'config/axiosConfig';
import EntityAvatar from '../Entities/EntityAvatar';
import FollowButton from 'components/Common/FollowButton';
import FollowersModal from 'components/Common/FollowersModal';
import TeamMembersModal from './TeamMembersModal';
import JoinButton from 'components/Common/JoinButton';
import { getActorDetails, canViewTeamFollow } from 'util/getObjectDetails';
import AlignmentTile from 'components/Common/AlignmentTile';
import { PrivacyLevel } from 'util/userUtils';
import UserAlignmentModal from 'components/User/UserAlignmentModal';
import { 
    Carousel, 
    CarouselContent, 
    CarouselItem, 
    CarouselNext, 
    CarouselPrevious 
} from 'ui/Carousel';
import { Loader2, MoreHorizontal, Megaphone, Users } from 'lucide-react';
import AlignmentSummaryDisplay from 'components/Alignment/AlignmentSummaryDisplay';
import { apiRoutes } from "util/apiRoutes";
import MenuOrDrawer, { type MenuOrDrawerItem } from "ui/MenuOrDrawer";
import PromoteEntityModal from "components/Promotions/PromoteEntityModal";
import ContentAwardButton from "components/Common/ContentAwardButton";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";

interface SocialLink {
    type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit';
    label: string;
    url: string;
}

interface PropertiesPublic {
    avatar_url: string;
    avatar_key: string;
    creator_id: number;
    bio_links?: SocialLink[];
    bio_brief?: string | null;
    alignment?: Record<string, string>;
    is_private?: boolean;
}

interface TeamWithPublicProps extends Omit<Team, 'properties_public'> {
    properties_public: PropertiesPublic;
    about: any;
    post_count?: number;
    follower_count?: number;
    following_count?: number;
    member_count?: number;
    current_user_role?: string;
    viewer_relation?: string;
}

interface TeamProfilePlateProps {
    team: TeamWithPublicProps;
    isProfilePage?: boolean;
    isDetailPanelOpen?: boolean;
    onOpenPanelChange?: (open: boolean) => void;
}

// Define statement interface
interface AlignmentStatement {
    id: number;
    statement: string;
}

const TeamProfilePlate: React.FC<TeamProfilePlateProps> = ({ team, isProfilePage = false, isDetailPanelOpen, onOpenPanelChange = () => {} }) => {
    const { currentUser, awaitingUser, setShowAuthModal } = useUser();
    const [profileTeam, setProfileTeam] = useState<TeamWithPublicProps | null>(team);
    const [primaryIssue, setPrimaryIssue] = useState<Issue | null>(null);
    const [followerCount, setFollowerCount] = useState<number>(0);
    const [postCount, setPostCount] = useState<number>(0);
    const [memberCount, setMemberCount] = useState<number>(0);
    const [creatorDetails, setCreatorDetails] = useState<any>(null);
    const [canViewFollow, setCanViewFollow] = useState<boolean>(false);
    const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [showMembersModal, setShowMembersModal] = useState(false);

    // Alignment state (new method based on member responses)
    const [isLoadingStatements, setIsLoadingStatements] = useState<boolean>(true);
    const [statementsError, setStatementsError] = useState<string | null>(null);
    const [showAlignmentModal, setShowAlignmentModal] = useState(false);
    const [statementNumber, setStatementNumber] = useState<number | null>(null);
    const [carouselApi, setCarouselApi] = useState<any>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [teamAlignmentMap, setTeamAlignmentMap] = useState<Record<number, { responses: number[]; importances: number[]; teabate?: any }>>({});
    const [currentUserAlignmentMap, setCurrentUserAlignmentMap] = useState<Record<number, { response: number; importance: number }>>({});
    const [currentUserTeabateTitles, setCurrentUserTeabateTitles] = useState<Record<number, string>>({});
    const [alignmentStatements, setAlignmentStatements] = useState<AlignmentStatement[]>([]);
    const [teamMembers, setTeamMembers] = useState<any[]>([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(true);

    // Promote-on-ad-breaks state. A moderator (including the creator)
    // sees a three-dots menu that opens the stake modal, funding the
    // promotion from their personal balance.
    const [teamMenuOpen, setTeamMenuOpen] = useState(false);
    const [promoteModalOpen, setPromoteModalOpen] = useState(false);
    const teamIsPrivate = team.properties_public?.is_private === true;
    const isTeamCreator = Boolean(currentUser?.id)
        && currentUser?.id === team.properties_public.creator_id;
    const isTeamModerator = team.current_user_role === "moderator";
    const canManageTeam = isTeamCreator || isTeamModerator;
    const canPromoteTeam = canManageTeam && !teamIsPrivate && Boolean(currentUser);
    const storyUi = getStoryUiStrings(resolveStoryLocale());

    // Fetch member alignments and aggregate per teabate id
    useEffect(() => {
        const fetchTeamAlignments = async () => {
            try {
                setIsLoadingStatements(true);
                if (!teamMembers || teamMembers.length === 0) {
                    setTeamAlignmentMap({});
                    setAlignmentStatements([]);
                    return;
                }

                // 1) Fetch each member's alignments
                const memberAlignments = await Promise.all(
                    teamMembers.map((m: any) =>
                        api.get(apiRoutes.user.userAlignments(m.id)).catch(() => ({ data: { relations: [] } }))
                    )
                );

                const aggregated: Record<number, { responses: number[]; importances: number[]; teabate?: any }> = {};
                const allTeabateIds: number[] = [];
                memberAlignments.forEach((res) => {
                    const relations = res.data?.relations || [];
                    relations.forEach((rel: any) => {
                        const id: number = rel.rel_node_id;
                        allTeabateIds.push(id);
                        if (!aggregated[id]) {
                            aggregated[id] = { responses: [], importances: [] };
                        }
                        aggregated[id].responses.push(rel.properties?.response ?? 0);
                        aggregated[id].importances.push(rel.properties?.importance ?? 0);
                    });
                });

                // 2) Fetch teabate details
                const uniqueIds = Array.from(new Set(allTeabateIds));
                const teabateResponses = await Promise.all(
                    uniqueIds.map((id) => api.get(apiRoutes.posts.byId(id)).catch(() => null))
                );
                const detailsMap = new Map<number, any>();
                teabateResponses.forEach((r) => { if (r?.data) detailsMap.set(r.data.id, r.data); });

                // 3) Build statements and attach teabate data
                const stmts: AlignmentStatement[] = [];
                Object.entries(aggregated).forEach(([idStr, agg]) => {
                    const id = Number(idStr);
                    const teabate = detailsMap.get(id);
                    if (teabate && teabate.properties?.dimension && teabate.properties.dimension !== 'none') {
                        (agg as any).teabate = teabate;
                        stmts.push({ id, statement: teabate.title });
                    }
                });

                setTeamAlignmentMap(aggregated);
                setAlignmentStatements(stmts.sort((a, b) => a.id - b.id));

                // 4) Current viewer alignments (optional markers)
                if (currentUser?.id) {
                    try {
                        const viewerRes = await api.get(apiRoutes.user.userAlignments(currentUser.id));
                        const viewMap: Record<number, { response: number; importance: number }> = {};
                        (viewerRes.data?.relations || []).forEach((rel: any) => {
                            viewMap[rel.rel_node_id] = {
                                response: rel.properties?.response ?? 0,
                                importance: rel.properties?.importance ?? 0,
                            };
                        });
                        setCurrentUserAlignmentMap(viewMap);
                        // Preload titles for current user's teabates used in viewer-only items
                        const ids = Object.keys(viewMap).map((k) => Number(k));
                        if (ids.length > 0) {
                            const details = await Promise.all(
                                ids.map((id) => api.get(apiRoutes.posts.byId(id)).catch(() => null))
                            );
                            const titleMap: Record<number, string> = {};
                            details.forEach((res) => {
                                if (res?.data && res.data.properties?.dimension && res.data.properties.dimension !== 'none') {
                                    titleMap[res.data.id] = res.data.title || "";
                                }
                            });
                            setCurrentUserTeabateTitles(titleMap);
                        } else {
                            setCurrentUserTeabateTitles({});
                        }
                    } catch {}
                } else {
                    setCurrentUserAlignmentMap({});
                    setCurrentUserTeabateTitles({});
                }
            } catch (error) {
                console.error('Error fetching team alignments:', error);
                setStatementsError('Failed to load alignments');
            } finally {
                setIsLoadingStatements(false);
            }
        };

        fetchTeamAlignments();
    }, [teamMembers, currentUser]);

    useEffect(() => {
        const fetchTeam = async () => {
            // Only fetch if we have a definitive user state (logged in or not)
            if (awaitingUser) {
                return;
            }
            if (!awaitingUser) {
                try {
                    const response = await api.get<TeamWithPublicProps>(apiRoutes.team.byName(team.teamname));
                    setProfileTeam(response.data);
                    setCanViewFollow(await canViewTeamFollow(response.data));

                    // Fetch additional data in parallel
                    const [issueResponse, followerCountResponse, memberCountResponse, creatorDetails] = await Promise.all([
                        response.data.about?.bio_issue?.value ? api.get<Issue>(apiRoutes.issues.byId(parseInt(response.data.about.bio_issue.value, 10))) : null,
                        api.get<{ count: number }>(apiRoutes.follow.followersCount("team", team.id)),
                        api.get<{ count: number }>(apiRoutes.team.memberCount(team.id)),
                        getActorDetails('user', response.data.properties_public.creator_id),
                    ]);

                    if (issueResponse) {
                        setPrimaryIssue(issueResponse.data);
                    }
                    setFollowerCount(followerCountResponse.data.count);
                    setMemberCount(memberCountResponse.data.count);
                    setCreatorDetails(creatorDetails);
                    const spillCount = await api.get<{ total: number }>(apiRoutes.entities.root, {
                            params: {
                                entityType: 'comment,impact,solution',
                                count: true,
                                coauthorId: team.id
                        }
                    });
                    setPostCount(spillCount.data.total);
                    
                    // Fetch team members
                    try {
                        setIsLoadingMembers(true);
                        const membersResponse = await api.get(apiRoutes.team.members(team.id));
                        //code was incorrectly using user_id (which doesn't exist on the member object) instead of member_id
                        const memberUserPromises = membersResponse.data.map((member: any) => 
                            api.get(apiRoutes.user.byId(member.member_id)).catch(() => null)
                        );
                        const memberUsers = await Promise.all(memberUserPromises);
                        const validMembers = memberUsers.filter(m => m !== null).map(m => m.data);
                        setTeamMembers(validMembers);
                    } catch (error) {
                        console.error('Error fetching team members:', error);
                    } finally {
                        setIsLoadingMembers(false);
                    }
                } catch (error) {
                    console.error('Error fetching team:', error);
                }
            }
        };

        fetchTeam();
    }, [team, awaitingUser, team.id, team.teamname]);

    const getLinkIcon = (type: string, url: string) => {
        if (!type) return null;
    
        switch (true) {
          case type === 'youtube':
            return (
              <span className={`size-4`}>
                <img className={`size-4`} src={`${IMG_CDN}/icons/youtube_icon.svg`} alt="YouTube" />
              </span>
            );
          case type === 'tiktok':
            return (
                <span className={`size-4`}>
                  <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/tiktok_icon.svg`} alt="TikTok" />
                </span>
            );
          case type === 'instagram':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/instagram_icon.png`} alt="Instagram" />
                </span>
            );
          case type === 'x':
            return  (
                <span className={`size-4`}>
                  <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/x_icon.png`} alt="X" />
                </span>
            );
          case type === 'facebook':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/facebook_icon.svg`} alt="Facebook" />
                </span>
            );
          case type === 'reddit':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/reddit_icon.svg`} alt="Reddit" />
                </span>
            );
          case type === 'custom':
            try {
                const linkHostname = new URL(url).hostname;
                if (linkHostname) {
                    return (
                        <span className={`size-4`}>
                            <img 
                                className={`size-4 rounded-sm`} 
                                src={`https://icons.duckduckgo.com/ip2/${linkHostname}.ico`} 
                                alt={`${linkHostname} icon`} 
                                onError={(e) => {
                                    const target = e.currentTarget;
                                    target.onerror = null; // Prevent infinite fallback loop
                                    
                                    // Try fallback services in order
                                    const services = [
                                        `https://www.google.com/s2/favicons?domain_url=${linkHostname}`,
                                        `https://favicon.yandex.net/favicon/${linkHostname}`,
                                        `https://f1.allesedv.com/16/${linkHostname}`,
                                        `${IMG_CDN}/icons/link_icon.svg`
                                    ];
                                    
                                    let currentIndex = 0;
                                    const tryNextService = () => {
                                        if (currentIndex < services.length) {
                                            target.src = services[currentIndex];
                                            currentIndex++;
                                            if (currentIndex < services.length) {
                                                target.onerror = tryNextService;
                                            }
                                        }
                                    };
                                    
                                    tryNextService();
                                }}
                            />
                        </span>
                    );
                }
            } catch (error) {
                console.error("Error parsing URL:", error);
            }
            // Default icon if URL parsing fails
            return (
                <span className={`size-4`}>
                    <img className={`size-4 rounded-sm`} src={`${IMG_CDN}/icons/link_icon.svg`} alt="Link" />
                </span>
            );
          default:    
            return null;
        }
    };

    const handleFollowChange = (isFollowing: boolean) => {
        if (profileTeam) {
            setProfileTeam({
                ...profileTeam,
                follower_count: (profileTeam.follower_count || 0) + (isFollowing ? 1 : -1)
            });
        }
    };

    const handleMemberChange = (isJoined: boolean) => {
        if (profileTeam) {
            setProfileTeam({
                ...profileTeam,
                member_count: (profileTeam.member_count || 0) + (isJoined ? 1 : -1)
            });
        }
    };

    const ManageTeam = () => {
        if (currentUser?.id === team.properties_public.creator_id) {
        return (
            <div className="flex flex-row gap-2">
                <Link
                    to={`/partea/${team.teamname}/settings/profile`}
                    className="flex flex-row flex-shrink-0 shadow-sm items-center bg-transparent border-2 px-3 py-1 font-semibold text-eoe-text dark:text-eoe-dark-text border-eoe-text dark:border-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover rounded-full text-sm w-fit"
                >
                    <span className="text-eoe-text dark:text-eoe-dark-text">Manage</span>
                </Link>
            </div>
        );
        }
        return null;
    }
    
    // Update handleRespond to include the auth check
    const handleRespond = (statementId: number) => {
        if (!awaitingUser && !currentUser) {
            setShowAuthModal(true);
        } else {
            setStatementNumber(statementId);
            setShowAlignmentModal(true);
        }
    };

    const handlePanelOpenChange = (open: boolean) => {
        setIsPanelOpen(open);
    };

    // Function to handle alignment statement selection
    const handleAlignmentStatementChange = (statementId: number | string) => {
        if (statementId === "summary") {
            // Navigate to summary - since summary is in a separate carousel content, we scroll to start
            if (carouselApi) {
                carouselApi.scrollTo(0);
            }
            return;
        }
        
        // Convert to number if it's a string
        const numericId = typeof statementId === 'string' ? parseInt(statementId, 10) : statementId;
        
        // Get answered and unanswered statements in the same order as they appear in the carousel
        const answeredStatements: number[] = [];
        const unansweredStatements: number[] = [];
        
        alignmentStatements.forEach((statement) => {
            const alignmentData = getAlignmentData(statement.id);
            if (!alignmentData) return;
            
            const currentUserData = getCurrentUserAlignmentData(statement.id);
            const hasResponded = currentUserData?.response !== null && 
                               currentUserData?.response !== undefined;
            
            if (hasResponded) {
                answeredStatements.push(statement.id);
            } else {
                unansweredStatements.push(statement.id);
            }
        });
        
        // Combine in the same order as the carousel items
        const visibleStatementIds = [...answeredStatements, ...unansweredStatements];
        
        // Find the index in the combined array
        const index = visibleStatementIds.findIndex(id => id === numericId);
        if (index !== -1 && carouselApi) {
            // Navigate directly to the index (no need to add 1 since summary is in separate content)
            carouselApi.scrollTo(index);
        }
    };

    // Function to render alignment tiles for statements with responses
    const renderAlignmentTiles = () => {
        // Permission gate: only members/followers of private teams may view perspective
        const isPrivate = profileTeam?.properties_public?.is_private === true;
        const canViewPerspective = !isPrivate || canViewFollow === true;
        if (!canViewPerspective) {
            return (
                <div className="text-sm text-center py-4">
                    Only members and followers of private Parteas may view their perspective
                </div>
            );
        }

        if (isLoadingStatements || isLoadingMembers) {
            return (
                <div className="flex justify-center items-center p-4">
                    <Loader2 className="size-4 animate-spin" />
                </div>
            );
        }

        if (statementsError || alignmentStatements.length === 0) {
            if (statementsError) {
            return (
                <div className="text-sm text-center py-2">
                    {statementsError}
                </div>
            );
            }
            return (
                <div className="text-sm text-center py-2">
                    The user hasn't shared any perspectives yet
                </div>
            );
        }

        // Create arrays to hold answered and unanswered items separately
        const answeredItems: React.ReactNode[] = [];
        const unansweredItems: React.ReactNode[] = [];
        
        // Create arrays to store visible statements for the dropdown, also separated
        const answeredStatements: Array<{id: number, statement: string, hasResponse: boolean}> = [];
        const unansweredStatements: Array<{id: number, statement: string, hasResponse: boolean}> = [];
        
        // Loop through statements and categorize them based on whether the user has responded
        alignmentStatements.forEach((statement) => {
            const statementId = statement.id;
            const alignmentData = getAlignmentData(statementId);
            
            if (!alignmentData) {
                return; // Skip this iteration if no data
            }
            
            const currentUserData = getCurrentUserAlignmentData(statementId);
            const hasResponded = currentUserData?.response !== null && 
                               currentUserData?.response !== undefined;
            
            // Create the statement object
            const statementObj = {
                id: statementId,
                statement: statement.statement,
                hasResponse: hasResponded
            };
            
            // Create the carousel item
            const carouselItem = (
                <CarouselItem key={statementId}>
                    <AlignmentTile
                        statement={statement.statement}
                        statementNumber={statementId}
                        response={alignmentData.response}
                        importance={alignmentData.importance}
                        privacy={undefined as any}
                        currentUserResponse={currentUserData?.response}
                        currentUserImportance={currentUserData?.importance}
                        currentUserViewerRelation={profileTeam?.viewer_relation as ViewerRelation}
                        isPanelOpen={isPanelOpen}
                        onPanelOpenChange={handlePanelOpenChange}
                        actorType="team"
                        onRespond={() => handleRespond(statementId)}
                        hasResponded={hasResponded}
                        statements={[...answeredStatements, ...unansweredStatements]} // This will be updated before rendering
                        onStatementChange={handleAlignmentStatementChange}
                        showSummaryOption={true}
                        targetActorId={profileTeam?.id}
                        targetActorName={team.teamname}
                    />
                </CarouselItem>
            );
            
            // Add to the appropriate arrays based on whether the user has responded
            if (hasResponded) {
                answeredStatements.push(statementObj);
                answeredItems.push(carouselItem);
            } else {
                unansweredStatements.push(statementObj);
                unansweredItems.push(carouselItem);
            }
        });
        
        // Combine the arrays in the desired order: answered first, then unanswered
        const visibleStatements = [...answeredStatements, ...unansweredStatements];
        const renderedItems = [...answeredItems, ...unansweredItems];

        // Append currentUser-only statements (not answered by team members)
        try {
            const existingIds = new Set(visibleStatements.map((s) => s.id));
            const viewerOnlyEntries = Object.entries(currentUserAlignmentMap)
                .map(([idStr, data]) => ({ id: Number(idStr), data }))
                .filter(({ id }) => !existingIds.has(id));

            viewerOnlyEntries.forEach(({ id }) => {
                const title = currentUserTeabateTitles[id];
                if (!title) return; // skip if not a valid perspective teabate or title not yet loaded
                const currentUserData = getCurrentUserAlignmentData(id);
                const hasResponded = currentUserData?.response !== null && currentUserData?.response !== undefined;

                const carouselItem = (
                    <CarouselItem key={`viewer-only-${id}`}>
                        <AlignmentTile
                            statement={title}
                            statementNumber={id}
                            // No team response; render as unselected (targetHasResponse=false)
                            response={0}
                            importance={0}
                            targetHasResponse={false}
                            privacy={undefined as any}
                            currentUserResponse={currentUserData?.response}
                            currentUserImportance={currentUserData?.importance}
                            currentUserViewerRelation={profileTeam?.viewer_relation as ViewerRelation}
                            isPanelOpen={isPanelOpen}
                            onPanelOpenChange={handlePanelOpenChange}
                            actorType="team"
                            onRespond={() => handleRespond(id)}
                            hasResponded={!!hasResponded}
                            // Keep dropdown unchanged
                            statements={visibleStatements}
                            onStatementChange={handleAlignmentStatementChange}
                            showSummaryOption={true}
                            targetActorId={profileTeam?.id}
                            targetActorName={team.teamname}
                        />
                    </CarouselItem>
                );
                renderedItems.push(carouselItem);
            });
        } catch (e) {
            // noop
        }
        
        // Only return the carousel if we have items to display
        if (renderedItems.length === 0 && teamMembers.length === 0) {
            return null;
        }
        
        // Create the summary tile only if we have team members
        const summaryTile = teamMembers.length > 0 ? (
            <CarouselItem key="summary">
                <AlignmentSummaryDisplay
                    targetUsers={teamMembers}
                    currentUser={currentUser}
                    targetLabel={profileTeam?.teamname || ""}
                    currentUserViewerRelation={profileTeam?.viewer_relation as ViewerRelation}
                    onStatementSelect={handleAlignmentStatementChange}
                    isTeam={true}
                />
            </CarouselItem>
        ) : null;
        
        // Update the statements prop for each carousel item with the complete list
        const finalRenderedItems = renderedItems.map((item, index) => {
            // Clone the item and update its props
            return React.cloneElement(item as React.ReactElement, {
                key: `statement-${index}`,
                children: React.cloneElement((item as React.ReactElement).props.children as React.ReactElement, {
                    statements: visibleStatements
                })
            });
        });
        
        // If we only have the summary tile, show it without the additional carousel items
        if (summaryTile && finalRenderedItems.length === 0) {
            return (
                <div className="w-full">
                    <AlignmentSummaryDisplay
                        targetUsers={teamMembers}
                        currentUser={currentUser}
                        targetLabel={profileTeam?.teamname || ""}
                        currentUserViewerRelation={profileTeam?.viewer_relation as ViewerRelation}
                        onStatementSelect={handleAlignmentStatementChange}
                        isTeam={true}
                    />
                </div>
            );
        }
        
        return (
            <Carousel 
                className="w-full space-y-2"
                setApi={setCarouselApi}
            >
                {/* Summary in first content */}
                <CarouselContent className="max-w-[90vw]">
                    {summaryTile}
                </CarouselContent>
                <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub text-left font-semibold items-left px-1">
                    <span className="text-eoe-text dark:text-eoe-dark-text">Responses</span>
                </div>
                {/* Responses in second content */}
                <CarouselContent className="max-w-[90vw]">
                    {finalRenderedItems}
                </CarouselContent>
                <div className="flex flex-row px-1 justify-between">
                    <CarouselPrevious size="lg" />
                    <CarouselNext size="lg" />
                </div>
            </Carousel>
        );
    };

    // New: compute aggregated response/importance for a statement from team members
    const getAlignmentData = (statementId: number) => {
        const entry = teamAlignmentMap[statementId];
        if (!entry) return null;
        // Average responses and importances
        const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
        const avgResponse = avg(entry.responses);
        const avgImportance = avg(entry.importances);

        // Normalize response to -2..2 by rounding to nearest integer per spec (e.g., 0.5 -> 1)
        const normalizedResponse = Math.max(-2, Math.min(2, Math.round(avgResponse)));
        const normalizedImportance = Math.max(-2, Math.min(2, Math.round(avgImportance)));

        return {
            response: normalizedResponse,
            importance: normalizedImportance,
            note: undefined,
            privacy: 'public' as PrivacyLevel
        };
    };

    const getCurrentUserAlignmentData = (statementId: number) => {
        const entry = currentUserAlignmentMap[statementId];
        if (!entry) return null;
        return {
            response: entry.response,
            importance: entry.importance
        };
    };

    return (
        <div className="bg-eoe-bg dark:bg-eoe-dark-bg">
            <div className="max-w-5xl mx-auto px-2 py-2">
                {/* Profile Header */}
                <div className="flex flex-col items-left space-y-4">
                    {/* Avatar */}
                    {/*
                     * Team name + members icon links to the partea
                     * profile (consistent "name click = profile" rule).
                     * Kept as a hand-rolled <a> here because the icon
                     * sits inside the link with a custom layout; the
                     * canonical `<ActorName>` renders text-only links.
                     * Avatar below uses the standardized `<ActorAvatar>`.
                     * Teams cannot have stories yet, so the ring never
                     * appears, but routing stays unified through the
                     * shared resolver.
                     */}
                    <a href={`/partea/${profileTeam?.teamname}`} className="flex flex-row items-start gap-1 text-xl font-bold text-eoe-text-heading dark:text-eoe-dark-text-heading break-all">
                        <Users className={`mt-1 size-5 flex-shrink-0 ${profileTeam?.current_user_role ? 'text-green-600' : 'text-eoe-gray dark:text-eoe-dark-text'}`} />
                        <span>{profileTeam?.teamname}</span>
                    </a>

                    <div className="flex-row grid grid-cols-3 sm:flex sm:flex-row items-center gap-2 sm:gap-16">
                        <div className="col-span-1">
                            <ActorAvatar
                                actor={toActorFromTeam(profileTeam)}
                                size={ACTOR_AVATAR_SIZES.profile}
                            />
                        </div>

                        {/* Stats */}
                        <div className="flex flex-row gap-4 justify-between sm:justify-start col-span-2 text-sm">
                            <Link to={`/partea/${profileTeam?.teamname}/overview`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {postCount || 0}
                                    </span>
                                    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        spills
                                    </span>
                                </div>
                            </Link>
                            <button onClick={() => setShowFollowersModal(canViewFollow)} className={`${canViewFollow ? '' : 'cursor-default'}`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {followerCount}
                                    </span>
                                    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        followers
                                    </span>
                                </div>
                            </button>
                            <button onClick={() => setShowMembersModal(canViewFollow)} className={`${canViewFollow ? '' : 'cursor-default'}`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {memberCount}
                                    </span>
                                    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        members
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Name and Description */}
                    <div className="text-left">
                        {profileTeam?.display_name && (
                            <h1 className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                                {profileTeam?.display_name}
                            </h1>
                        )}
                        {profileTeam?.about?.bio_brief?.value && (
                            <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub whitespace-normal">
                                {profileTeam?.about?.bio_brief?.value}
                            </p>
                        )}
                    </div>

                    {/* Edit Profile/Follow Button */}
                    <div className="flex flex-row flex-wrap items-center gap-2">
                        <FollowButton
                            entityId={team.id}
                            entityType="team"
                            entityName={team.teamname}
                            showAllOptions={true}
                            isPrivate={team.properties_public?.is_private}
                            onFollowChange={handleFollowChange}
                        />
                        <JoinButton
                            teamId={team.id}
                            teamName={team.teamname}
                            isPrivate={team.properties_public?.is_private}
                            onJoinChange={handleMemberChange}
                        />
                        {!awaitingUser && (
                                <ManageTeam />
                        )}
                        {canPromoteTeam ? (
                            <MenuOrDrawer
                                open={teamMenuOpen}
                                onOpenChange={setTeamMenuOpen}
                                title={storyUi.promoteMenuTitle}
                                trigger={(
                                    <button
                                        type="button"
                                        aria-label={storyUi.profileMoreActionsLabel}
                                        aria-haspopup="menu"
                                        aria-expanded={teamMenuOpen}
                                        onClick={() => setTeamMenuOpen(true)}
                                        className="flex items-center justify-center h-8 w-8 rounded-full border-2 border-eoe-text dark:border-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text dark:text-eoe-dark-text"
                                    >
                                        <MoreHorizontal className="size-4" aria-hidden="true" />
                                    </button>
                                )}
                                items={[
                                    {
                                        id: "promote-team",
                                        label: storyUi.promoteParteaOnAdBreaks,
                                        icon: Megaphone,
                                        onSelect: () => setPromoteModalOpen(true),
                                    } satisfies MenuOrDrawerItem,
                                ]}
                            />
                        ) : null}
                        {!teamIsPrivate && currentUser && !canManageTeam ? (
                            <ContentAwardButton
                                entityType="team"
                                entityId={team.id}
                                authorUserId={null}
                            />
                        ) : null}
                    </div>
                    {promoteModalOpen ? (
                        <PromoteEntityModal
                            isOpen={promoteModalOpen}
                            onClose={() => setPromoteModalOpen(false)}
                            entityType="team"
                            entityId={team.id}
                            entityLabel={team.display_name || team.teamname || null}
                            sponsor={{
                                actorType: "user",
                                actorId: currentUser?.id ?? 0,
                                label: currentUser?.username
                                    ? `@${currentUser.username}`
                                    : undefined,
                            }}
                        />
                    ) : null}
                    {/* Primary Issue */}
                    {primaryIssue && (
                        <EntityAvatar
                            entity={primaryIssue}
                            entityType="issue"
                            entityChip={true}
                            showCloseChip={false}
                            showLink={true}
                            className="pr-2 bg-eoe-bg-modal"
                            badgeText="#1 Issue"
                        />
                    )}
                    
                    
                    {/* About Section */}
                    <DetailPanel 
                        title="About" 
                        className="mt-2 bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                        showTopCollapse={false}
                        showBottomCollapse={true}
                        headingContent={
                            <div className="flex flex-col items-start gap-2">                        
                                {/* Social Links */}
                                    {profileTeam?.about?.bio_links?.value && (() => {
                                        try {
                                            const links = profileTeam.about.bio_links.value;
                                            if (links && links.length > 0) {
                                                return (
                                                    <div className="flex flex-wrap gap-1">
                                                        {links.map((link: SocialLink, index: number) => (
                                                            <a
                                                                key={index}
                                                                href={link.url}
                                                                target="_blank"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    window.open(link.url, '_blank');
                                                                }}
                                                                rel="noopener noreferrer"
                                                                className="flex flex-row gap-1 items-center btn-secondary bg-slate-500 dark:bg-slate-800 hover:bg-slate-400 dark:hover:bg-slate-700 py-1 text-xs"
                                                            >
                                                                {getLinkIcon(link.type, link.url)}
                                                                {link.label}
                                                            </a>
                                                        ))}
                                                    </div>
                                                );
                                            }
                                        } catch (error) {
                                            console.error('Error parsing social links:', error);
                                        }
                                        return null;
                                    })
                                ()}
                            </div>
                        }
                    >
                        {/* Leader */}
                        {creatorDetails && (
                            <EntityAvatar
                                entity={creatorDetails}
                                entityType="user"
                                entityChip={true}
                                showCloseChip={false}
                                showLink={true}
                                className="bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover"
                                badgeText="Founder"
                            />
                        )}
                        {/* Alignment Tiles 
                        <p className="text-xs font-semibold text-eoe-text-sub dark:text-eoe-dark-text-sub mb-2 pt-2">
                            Perspective
                        </p>*/}
                        {renderAlignmentTiles()}
                    </DetailPanel>
                </div>
            </div>

            {/* Followers Modal */}
            {showFollowersModal && (
                <FollowersModal
                    entityId={team.id}
                    entityType="team"
                    viewType="followers"
                    onClose={() => setShowFollowersModal(false)}
                />
            )}

            {/* Members Modal */}
            {showMembersModal && (
                <TeamMembersModal
                    teamId={team.id}
                    teamName={team.teamname}
                    isTeamModerator={team.current_user_role === 'moderator'}
                    onClose={() => setShowMembersModal(false)}
                />
            )}
            {showAlignmentModal && (
                <UserAlignmentModal
                    onClose={() => setShowAlignmentModal(false)}
                    initialStatementId={statementNumber}
                />
            )}
        </div>
    );
};

export default TeamProfilePlate; 